<template>
  <div class="content-wrapper view-patient-group-manage p-0">
    <div class="content">
      <div class="card">
        <div class="card-body">
          <h4><i class="fas fa-users"></i> จัดการกลุ่มผู้ป่วย</h4>
          <div class="row mx-1 my-3">
            <b-form-input
              type="search"
              class="col required"
              autofocus
              v-model="filter"
              placeholder="ค้นหากลุ่มจากชื่อ"
            ></b-form-input>
            <span
              v-b-tooltip.hover="canAddGroup ? '' : `แพ็คเกจฟรีสร้างได้สูงสุด ${groupLimit} กลุ่ม — สมัคร subscription เพื่อสร้างเพิ่ม`"
              :tabindex="canAddGroup ? -1 : 0"
              style="display: inline-block;"
            >
              <b-button
                class="ml-3 text-nowrap"
                variant="primary"
                :disabled="!canAddGroup"
                @click="openCreateModal"
              >
                <i class="fas fa-plus"></i> เพิ่มกลุ่ม
                <small v-if="groupLimit !== null" class="ml-1 opacity-75">
                  ({{ groupCount }}/{{ groupLimit }})
                </small>
              </b-button>
            </span>
          </div>

          <b-table
            id="patientGroupTable"
            ref="patientGroupTable"
            hover
            small
            sort-icon-left
            head-variant="light"
            :items="groupList"
            :fields="fields"
            :filter="filter"
            responsive
            table-class="compact-list-table"
            show-empty
            empty-text="ไม่มีกลุ่มผู้ป่วย"
            @row-clicked="onRowClick"
          >
            <template v-slot:cell(index)="row">{{ row.index + 1 }}.</template>
            <template #cell(memberCount)="row">
              {{ row.item.memberCount || 0 }} คน
            </template>
            <template v-slot:cell(buttons)="row">
              <b-button-group class="show-when-hovered" size="xs">
                <b-button
                  variant="outline-primary"
                  @click.stop="openMemberModal(row.item)"
                >
                  <i class="fas fa-users"></i>
                </b-button>
                <b-button
                  variant="outline-secondary"
                  @click.stop="openEditModal(row.item)"
                >
                  <i class="fas fa-edit"></i>
                </b-button>
                <b-button
                  variant="outline-danger"
                  @click.stop="confirmDelete(row.item)"
                >
                  <i class="fas fa-trash-alt"></i>
                </b-button>
              </b-button-group>
            </template>
          </b-table>
        </div>
      </div>

      <!-- Create / Edit Modal -->
      <b-modal
        ref="groupFormModal"
        :title="isEdit ? 'แก้ไขกลุ่มผู้ป่วย' : 'เพิ่มกลุ่มผู้ป่วย'"
        ok-title="บันทึก"
        cancel-title="ยกเลิก"
        @ok="handleSave"
      >
        <b-form>
          <b-form-group label="ชื่อกลุ่ม *" label-for="group-name">
            <b-form-input
              id="group-name"
              v-model="form.name"
              placeholder="เช่น ผู้สูงอายุ, ข้าราชการ"
              required
            />
          </b-form-group>
          <b-form-group label="คำอธิบาย" label-for="group-desc">
            <b-form-textarea
              id="group-desc"
              v-model="form.description"
              rows="2"
              placeholder="รายละเอียดเพิ่มเติม (ไม่บังคับ)"
            />
          </b-form-group>
        </b-form>
      </b-modal>

      <!-- Member Management Modal -->
      <b-modal
        ref="memberModal"
        :title="selectedGroup ? `สมาชิกกลุ่ม: ${selectedGroup.name}` : 'สมาชิกกลุ่ม'"
        size="lg"
        ok-title="ปิด"
        ok-only
        @show="loadGroupMembers"
      >
        <div v-if="selectedGroup">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <b-form-input
              v-model="memberSearch"
              placeholder="ค้นหาผู้ป่วย..."
              style="max-width: 300px"
              type="search"
            />
            <b-button variant="success" size="sm" @click="toggleAddSearch">
              <i class="fas fa-user-plus mr-1"></i> เพิ่มสมาชิก
            </b-button>
          </div>

          <!-- Add patient search -->
          <b-collapse v-model="showAddSearch">
            <div class="border rounded p-3 mb-3 bg-light">
              <h6 class="mb-2 text-primary">
                <i class="fas fa-search mr-1"></i> ค้นหาผู้ป่วยเพื่อเพิ่ม
              </h6>
              <b-form-input
                v-model="addPatientSearch"
                placeholder="ค้นหาชื่อหรือรหัสผู้ป่วย..."
                @input="searchPatients"
              />
              <b-list-group class="mt-2" v-if="searchResults.length > 0" style="max-height: 200px; overflow-y: auto">
                <b-list-group-item
                  v-for="patient in searchResults"
                  :key="patient.id"
                  class="d-flex justify-content-between align-items-center py-2"
                  button
                  @click="addPatientToGroup(patient)"
                >
                  <span>
                    <strong>{{ patient.firstNameTh }} {{ patient.lastNameTh }}</strong>
                    <small class="text-muted ml-2">{{ patient.hn }}</small>
                  </span>
                  <b-badge variant="primary">เพิ่ม</b-badge>
                </b-list-group-item>
              </b-list-group>
              <small v-else-if="addPatientSearch.length > 1" class="text-muted">ไม่พบผู้ป่วย</small>
            </div>
          </b-collapse>

          <!-- Member list -->
          <b-table
            :items="filteredMembers"
            :fields="memberFields"
            small
            hover
            show-empty
            empty-text="ไม่มีสมาชิกในกลุ่มนี้"
          >
            <template v-slot:cell(index)="row">{{ row.index + 1 }}.</template>
            <template #cell(name)="row">
              {{ row.item.firstNameTh }} {{ row.item.lastNameTh }}
            </template>
            <template #cell(buttons)="row">
              <b-button
                size="xs"
                variant="outline-danger"
                @click="removeMemberFromGroup(row.item)"
              >
                <i class="fas fa-times"></i>
              </b-button>
            </template>
          </b-table>
        </div>
      </b-modal>

      <Loading v-if="isLoading"></Loading>
      <Dialog ref="Dialog" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Loading from "@/components/Loading";
import Dialog from "@/components/modal/Dialog.vue";
import axios from "axios";
import { FEATURE } from "@/constants/entitlements";

export default {
  name: "PatientGroupManagement",
  components: { Loading, Dialog },
  data() {
    return {
      filter: "",
      isLoading: false,
      isEdit: false,
      editId: null,
      selectedGroup: null,
      groupMembers: [],
      memberSearch: "",
      showAddSearch: false,
      addPatientSearch: "",
      searchResults: [],
      form: this.defaultForm(),
      fields: [
        { key: "index", label: "#", class: "text-center col-index" },
        { key: "name", label: "ชื่อกลุ่ม", sortable: true, class: "col-name" },
        { key: "description", label: "คำอธิบาย", class: "col-name" },
        { key: "memberCount", label: "จำนวน", class: "col-num text-center" },
        { key: "buttons", label: "จัดการ", class: "text-center col-buttons" },
      ],
      memberFields: [
        { key: "index", label: "#", class: "text-center col-index" },
        { key: "name", label: "ชื่อ-นามสกุล" },
        { key: "hn", label: "HN" },
        { key: "buttons", label: "", class: "text-center" },
      ],
    };
  },
  computed: {
    ...mapGetters("modulePatientGroup", ["getGroupList"]),
    ...mapGetters("moduleBranch", ["entitlementLimit"]),
    groupList() {
      return this.getGroupList;
    },
    groupCount() {
      return (this.getGroupList || []).length;
    },
    groupLimit() {
      return this.entitlementLimit(FEATURE.PATIENT_GROUP);
    },
    canAddGroup() {
      const limit = this.groupLimit;
      if (limit === null) return true;
      return this.groupCount < limit;
    },
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    branchUrl() {
      return this.$route.params.branchUrl;
    },
    filteredMembers() {
      if (!this.memberSearch) return this.groupMembers;
      const q = this.memberSearch.toLowerCase();
      return this.groupMembers.filter(m =>
        (`${m.firstNameTh} ${m.lastNameTh}`).toLowerCase().includes(q) ||
        (m.hn || "").toLowerCase().includes(q)
      );
    },
  },
  mounted() {
    this.fetchGroups();
  },
  methods: {
    ...mapActions("modulePatientGroup", [
      "fetchGroupList",
      "createGroup",
      "updateGroup",
      "deleteGroup",
      "addMembers",
      "removeMembers",
    ]),
    defaultForm() {
      return {
        name: "",
        description: "",
      };
    },
    async fetchGroups() {
      this.isLoading = true;
      await this.fetchGroupList({
        clinicUrl: this.clinicUrl,
        branchUrl: this.branchUrl,
      });
      this.isLoading = false;
    },
    onRowClick(item) {
      this.openMemberModal(item);
    },
    openCreateModal() {
      this.isEdit = false;
      this.editId = null;
      this.form = this.defaultForm();
      this.$refs.groupFormModal.show();
    },
    openEditModal(group) {
      this.isEdit = true;
      this.editId = group.id;
      this.form = {
        name: group.name,
        description: group.description || "",
      };
      this.$refs.groupFormModal.show();
    },
    async handleSave(bvModalEvent) {
      bvModalEvent.preventDefault();

      // Defense-in-depth: matches the disabled "Add group" button above.
      // Cap comes from entitlementLimit (null = unlimited).
      // Editing existing groups is always allowed.
      if (!this.isEdit && !this.canAddGroup) {
        this.$refs.Dialog.showToast(
          "warning",
          `แพ็คเกจฟรีสร้างได้สูงสุด ${this.groupLimit} กลุ่ม — สมัคร subscription เพื่อสร้างเพิ่ม`
        );
        return;
      }

      if (!this.form.name.trim()) {
        this.$refs.Dialog.showToast("danger", "กรุณาระบุชื่อกลุ่ม");
        return;
      }

      try {
        if (this.isEdit) {
          await this.updateGroup({
            clinicUrl: this.clinicUrl,
            branchUrl: this.branchUrl,
            groupId: this.editId,
            data: this.form,
          });
        } else {
          await this.createGroup({
            clinicUrl: this.clinicUrl,
            branchUrl: this.branchUrl,
            data: this.form,
          });
        }

        this.$refs.Dialog.showToast(
          "success",
          this.isEdit ? "แก้ไขกลุ่มสำเร็จ" : "สร้างกลุ่มสำเร็จ"
        );
        this.$refs.groupFormModal.hide();
        await this.fetchGroups();
      } catch (err) {
        this.$refs.Dialog.showToast(
          "danger",
          err.response?.data?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง"
        );
      }
    },
    async confirmDelete(group) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `ต้องการลบกลุ่ม "${group.name}" ?`,
        { title: "ยืนยันการลบ", okVariant: "danger", okTitle: "ลบ", cancelTitle: "ยกเลิก" }
      );
      if (confirmed) {
        try {
          this.isLoading = true;
          await this.deleteGroup({
            clinicUrl: this.clinicUrl,
            branchUrl: this.branchUrl,
            groupId: group.id,
          });
          this.$refs.Dialog.showToast("success", "ลบกลุ่มสำเร็จ");
          await this.fetchGroups();
        } catch (err) {
          this.$refs.Dialog.showToast(
            "danger",
            err.response?.data?.message || "ลบกลุ่มไม่สำเร็จ กรุณาลองใหม่"
          );
          this.isLoading = false;
        }
      }
    },
    openMemberModal(group) {
      this.selectedGroup = group;
      this.memberSearch = "";
      this.showAddSearch = false;
      this.addPatientSearch = "";
      this.searchResults = [];
      this.$refs.memberModal.show();
    },
    async loadGroupMembers() {
      if (!this.selectedGroup) return;
      try {
        const res = await axios.get(
          `/api/patient-group/${this.clinicUrl}/${this.branchUrl}/${this.selectedGroup.id}/members`
        );
        this.groupMembers = res.data || [];
      } catch {
        this.groupMembers = this.selectedGroup.members || [];
      }
    },
    toggleAddSearch() {
      this.showAddSearch = !this.showAddSearch;
    },
    async searchPatients() {
      if (this.addPatientSearch.length < 2) {
        this.searchResults = [];
        return;
      }
      try {
        const res = await axios.get(
          `/api/patient/${this.clinicUrl}/${this.branchUrl}`,
          { params: { keyword: this.addPatientSearch, limit: 20 } }
        );
        const patients = res.data?.patients || res.data || [];
        const memberIds = this.groupMembers.map(m => m.id);
        this.searchResults = patients.filter(p => !memberIds.includes(p.id));
      } catch {
        this.searchResults = [];
      }
    },
    async addPatientToGroup(patient) {
      if (!this.selectedGroup) return;
      try {
        await this.addMembers({
          clinicUrl: this.clinicUrl,
          branchUrl: this.branchUrl,
          groupId: this.selectedGroup.id,
          patientIds: [patient.id],
        });
        this.$refs.Dialog.showToast("success", "เพิ่มสมาชิกสำเร็จ");
        this.showAddSearch = false;
        this.addPatientSearch = "";
        this.searchResults = [];
        await this.loadGroupMembers();
        await this.fetchGroups();
      } catch (err) {
        this.$refs.Dialog.showToast(
          "danger",
          err.response?.data?.message || "เพิ่มสมาชิกไม่สำเร็จ"
        );
      }
    },
    async removeMemberFromGroup(member) {
      if (!this.selectedGroup) return;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `ต้องการลบ "${member.firstNameTh} ${member.lastNameTh}" ออกจากกลุ่ม?`,
        { title: "ยืนยัน", okVariant: "danger", okTitle: "ลบ", cancelTitle: "ยกเลิก" }
      );
      if (!confirmed) return;
      try {
        await this.removeMembers({
          clinicUrl: this.clinicUrl,
          branchUrl: this.branchUrl,
          groupId: this.selectedGroup.id,
          patientIds: [member.id],
        });
        this.$refs.Dialog.showToast("success", "ลบสมาชิกสำเร็จ");
        await this.loadGroupMembers();
        await this.fetchGroups();
      } catch (err) {
        this.$refs.Dialog.showToast(
          "danger",
          err.response?.data?.message || "ลบสมาชิกไม่สำเร็จ"
        );
      }
    },
  },
};
</script>
